import styled from '@emotion/styled';
import { Typography, useTheme } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import { DoneCelebration } from '../DoneCelebration';

const GrayText = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500
}));

export const OptionalTasksHint = () => {
  const { t } = useUnsafeTranslation();
  return (
    <GrayText>
      {t(
        'These tasks have passed their deadline, but you can still complete them if you like.'
      )}
    </GrayText>
  );
};

export const DynamicallyTailoredListHint = () => {
  const { t } = useUnsafeTranslation();
  return (
    <GrayText>
      {t(
        'This list is dynamically tailored to you, so it will update as it adapts to your sitation.'
      )}
    </GrayText>
  );
};

export const NoTasksForYou = ({
  singleInstanceMode = false
}: {
  singleInstanceMode?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <GrayText>
        {singleInstanceMode
          ? t('There are currently no tasks for you in this experience.')
          : t('There are currently no tasks for you.')}
      </GrayText>
      <DynamicallyTailoredListHint />
    </>
  );
};

export const AllCurrentTasksDone = ({
  anyTasksAtAll,
  singleInstanceMode = false
}: {
  anyTasksAtAll: boolean;
  singleInstanceMode?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  if (!anyTasksAtAll) {
    return <NoTasksForYou singleInstanceMode={singleInstanceMode} />;
  }
  return (
    <DoneCelebration title={t(`You're done for now`)}>
      <Typography>{t('All your current tasks are completed')}</Typography>
    </DoneCelebration>
  );
};

export const NoCurrentTasksButOverdue = ({ to }: { to?: string }) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  return (
    <>
      <GrayText>{t('You currently have no tasks in this section.')}</GrayText>
      <GrayText>
        <span>{t('Other tasks are waiting for you in the')}</span>{' '}
        {to ? (
          <Link
            style={{
              color: theme.palette.primary.main
            }}
            to={to}
          >
            {t('Overdue section')}
          </Link>
        ) : (
          <span>{t('Overdue section')}</span>
        )}
        <span>.</span>
      </GrayText>
    </>
  );
};

export const NoOverdueTasks = () => {
  const { t } = useUnsafeTranslation();
  return <GrayText>{t('You have no overdue tasks.')}</GrayText>;
};

export const NoCompletedTasks = () => {
  const { t } = useUnsafeTranslation();
  return <GrayText>{t(`You haven't completed any tasks yet.`)}</GrayText>;
};

export const NoUpcomingTasks = () => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <GrayText>{t('There are currently no upcoming tasks for you.')}</GrayText>
      <DynamicallyTailoredListHint />
    </>
  );
};

export const NoOptionalTasks = () => {
  const { t } = useUnsafeTranslation();
  return (
    <GrayText>
      {t(`You have no optional tasks that have passed their deadline.`)}
    </GrayText>
  );
};

export const NoUpcomingMessages = () => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <GrayText>
        {t('There are currently no upcoming messages for you.')}
      </GrayText>
      <DynamicallyTailoredListHint />
    </>
  );
};

export const NoArchivedMessages = ({
  singleInstanceMode = false
}: {
  singleInstanceMode?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <GrayText>
      {singleInstanceMode
        ? t(`You have no archived messages in this experience.`)
        : t(`You have no archived messages.`)}
    </GrayText>
  );
};

export const NoUnarchivedMessages = () => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainerVertical fullWidth spacing={2}>
      <GrayText>{t('You have no un-archived messages.')}</GrayText>
      <GrayText>{t('Find previous messages in the Archive.')}</GrayText>
    </FlexContainerVertical>
  );
};

export const NoRecentMoments = () => {
  const { t } = useUnsafeTranslation();
  return <GrayText>{t('You have no unread recents news.')}</GrayText>;
};

export const NoArchivedMoments = () => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainerVertical fullWidth spacing={2}>
      <GrayText>{t('You have no read news.')}</GrayText>
      <GrayText>{t('Find archived news on your Dashboard.')}</GrayText>
    </FlexContainerVertical>
  );
};
